import { ACTIONS_TYPES } from 'redux/helper';
import { service } from 'services';
import {
  _tf, _t,
  basicCRUDAction, hotGetAction, isEmptyArray, isEmptyObject,
} from 'utils';

const retrieveByFunction = (e) => (isEmptyObject(e) || isEmptyArray(e.items) ? [] : e.items);

export const database = {
  postgresql: {
    getAll: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL,
      apiResult: service.database.postgresql.getAll(),
      retrieveByFunction,
      isRetrieveItems: true,
    }),

    create: async ({ properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.CREATE,
      apiResult: service.database.postgresql.create({ properties }),
    }),

    get: async ({ id }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET,
      apiResult: service.database.postgresql.get({ id }),
    }),

    update: async ({ id, properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.UPDATE,
      apiResult: service.database.postgresql.update({ id, properties }),
      isReFetch: true,
    }),

    delete: async ({ id }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.DELETE,
      apiResult: service.database.postgresql.delete({ id }),
    }),

    getAllVersions: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_VERSIONS,
      apiResult: service.database.postgresql.getAllVersions(),
      retrieveByFunction: (e) => e.data,
    }),

    getUpgradableVersions: async ({ clusterId }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_UPGRADABLE_VERSIONS,
      apiResult: service.database.postgresql.getUpgradableVersions({ clusterId }),
      retrieveByFunction: (e) => e.data,
    }),

    getAllBackups: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_BACKUPS,
      apiResult: service.database.postgresql.getAllBackups(),
      retrieveByFunction,
    }),

    getAllBackupsByClusterId: async ({ clusterId }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_BACKUPS_BY_CLUSTER_ID,
      apiResult: service.database.postgresql.getAllBackupsByClusterId({ clusterId }),
      retrieveByFunction,
    }),

    getBackupById: async ({ backupId }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_BACKUP_BY_ID,
      apiResult: service.database.postgresql.getBackupById({ backupId }),
    }),

    restoreBackup: async ({ clusterId, backupId, recoveryTargetTime }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.POSTGRESQL.RESTORE_BACKUP,
      apiResult: service.database.postgresql.restoreBackup({ clusterId, properties: { backupId, recoveryTargetTime } }),
    }),
  },

  mongodb: {
    getAll: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL,
      apiResult: service.database.mongodb.getAll(),
      retrieveByFunction,
      isRetrieveItems: true,
    }),

    create: async ({ properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.CREATE,
      apiResult: service.database.mongodb.create({ properties }),
    }),

    update: async ({ id, properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.UPDATE,
      apiResult: service.database.mongodb.update({ id, properties }),
      isReFetch: true,
    }),

    get: async ({ id }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET,
      apiResult: service.database.mongodb.get({ id }),
      retrieveByFunction: (e) => {
        e.properties.ram /= 1024;
        e.properties.storageSize /= 1024;
        return e;
      },
    }),

    delete: async ({ id }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.DELETE,
      apiResult: service.database.mongodb.delete({ id }),
    }),

    getAllVersions: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL_VERSIONS,
      apiResult: service.database.mongodb.getAllVersions(),
      retrieveByFunction: (e) => e.data,
    }),

    getTemplates: async () => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET_TEMPLATES,
      apiResult: service.database.mongodb.getTemplates(),
      retrieveByFunction: (response) => {
        const { items } = response;
        if (items.length === 0 || items[0]?.properties?.name) {
          return items;
        }
        return items.map(({
          id, name, edition, cores, ram, storageSize,
        }) => ({
          type: 'template',
          id,
          metadata: {},
          properties: {
            name,
            edition,
            cores,
            ram,
            storageSize,
          },
        }));
      },
    }),

    getAllMongoDBUsers: async ({ clusterId }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL_USERS,
      apiResult: service.database.mongodb.getUsersForCluster(clusterId),
      isRetrieveItems: true,
    }),

    createMongoDBUser: async ({ clusterId, properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.CREATE_USER,
      successMessage: _tf('DATABASES.MONGODB.ACTION_TEXTS.USER_CREATE_SUCCESS', { value: properties.username }),
      apiResult: service.database.mongodb.createUserForCluster(clusterId, properties),
    }),

    updateMongoDBUser: async ({ clusterId, username, properties }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.UPDATE_USER,
      successMessage: _tf('DATABASES.MONGODB.ACTION_TEXTS.USER_UPDATE_SUCCESS', { value: username }),
      apiResult: service.database.mongodb.updateUserForCluster({
        clusterId,
        username,
        properties,
      }),
    }),

    deleteMongoDbUser: async ({ clusterId, username }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.DELETE_USER,
      successMessage: _tf('DATABASES.MONGODB.ACTION_TEXTS.USER_DELETE_SUCCESS', { value: username }),
      apiResult: service.database.mongodb.deleteMongoDBUser({
        clusterId,
        username,
      }),
    }),

    getSnapshotsForCluster: async ({ clusterId }) => hotGetAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.GET_SNAPSHOTS_FOR_CLUSTER,
      apiResult: service.database.mongodb.getSnapshotsForCluster({ clusterId }),
      isRetrieveItems: true,
    }),

    restoreCluster: async ({ clusterId, snapshotId, recoveryTargetTime }) => basicCRUDAction({
      action: ACTIONS_TYPES.DATABASE.MONGODB.RESTORE_CLUSTER,
      successMessage: _t('DATABASES.MONGODB.ACTION_TEXTS.RESTORE_SUCCESS'),
      apiResult: service.database.mongodb.restoreCluster({ clusterId, snapshotId, recoveryTargetTime }),
    }),
  },

  getQuota: async () => hotGetAction({
    action: ACTIONS_TYPES.DATABASE.GET_QUOTA,
    apiResult: service.database.getQuota(),
    retrieveByFunction: (e) => {
      const {
        quotaLimits,
        quotaUsage,
      } = e;

      const mebibyteKeys = ['memory', 'storage'];

      const parserFn = (key, value) => {
        if (mebibyteKeys.includes(key)) {
          return value / 1024;
        }
        return parseInt(value, 10);
      };

      const quotaLimitsWithGibibyteValues = Object.keys(quotaLimits).reduce((acc, key) => {
        acc[key] = parserFn(key, quotaLimits[key]);
        return acc;
      }, {});

      const quotaUsageWithGibibyteValues = Object.keys(quotaUsage).reduce((acc, key) => {
        acc[key] = parserFn(key, quotaUsage[key]);
        return acc;
      }, {});

      return {
        quotaLimits: quotaLimitsWithGibibyteValues,
        quotaUsage: quotaUsageWithGibibyteValues,
      };
    },
  }),
};
